import type { CmmdBoardTask } from "@cmmd-forge/contracts";

/** A status column on the CMMD project planning board. */
export interface BoardColumn {
  readonly status: string;
  readonly label: string;
  readonly tasks: ReadonlyArray<CmmdBoardTask>;
}

interface ColumnSpec {
  readonly status: string;
  readonly label: string;
  /** Core columns render even when empty; optional ones only when populated. */
  readonly alwaysShow: boolean;
}

const COLUMN_ORDER: ReadonlyArray<ColumnSpec> = [
  { status: "backlog", label: "Backlog", alwaysShow: false },
  { status: "todo", label: "To do", alwaysShow: true },
  { status: "in_progress", label: "In progress", alwaysShow: true },
  { status: "in_review", label: "In review", alwaysShow: true },
  { status: "blocked", label: "Blocked", alwaysShow: false },
  { status: "done", label: "Done", alwaysShow: true },
  { status: "cancelled", label: "Cancelled", alwaysShow: false },
];

const KNOWN_STATUSES = new Set(COLUMN_ORDER.map((column) => column.status));

function boardStatusLabel(status: string): string {
  return COLUMN_ORDER.find((column) => column.status === status)?.label ?? "To do";
}

/**
 * The status group a single task belongs to (key, header label, column order).
 * Unknown statuses fall back to "To do", matching {@link groupBoardTasksByStatus}.
 * Shared by the board and the tasks DataTable so both agree on grouping.
 */
export function taskStatusDescriptor(task: CmmdBoardTask): {
  readonly key: string;
  readonly label: string;
  readonly order: number;
} {
  const status = KNOWN_STATUSES.has(task.status) ? task.status : "todo";
  const index = COLUMN_ORDER.findIndex((column) => column.status === status);
  return {
    key: status,
    label: boardStatusLabel(status),
    order: index < 0 ? COLUMN_ORDER.length : index,
  };
}

/**
 * Group root-level board tasks into ordered status columns. Tasks with an
 * unrecognized status fall back to "To do" rather than vanishing, so nothing is
 * silently dropped from the board.
 */
export function groupBoardTasksByStatus(
  tasks: ReadonlyArray<CmmdBoardTask>,
): ReadonlyArray<BoardColumn> {
  const byStatus = new Map<string, CmmdBoardTask[]>();
  for (const task of tasks) {
    const status = KNOWN_STATUSES.has(task.status) ? task.status : "todo";
    const bucket = byStatus.get(status) ?? [];
    bucket.push(task);
    byStatus.set(status, bucket);
  }

  return COLUMN_ORDER.filter(
    (column) => column.alwaysShow || (byStatus.get(column.status)?.length ?? 0) > 0,
  ).map((column) => ({
    status: column.status,
    label: column.label,
    tasks: byStatus.get(column.status) ?? [],
  }));
}

/** Whether a board task already has a Forge thread spawned from it. */
export function hasSpawnedThread(task: CmmdBoardTask): boolean {
  return task.threadId !== null;
}
